import React, { useState, useEffect } from 'react';
import { Shield, Search, X, ExternalLink, Plus, Code, Copy, CheckCircle } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAllValidations } from '../../services/validationService';

// Validation categories with icons and colors
const VALIDATION_CATEGORIES = [
  { id: 'STATIC', name: 'Static Rules', icon: '⚡', bgColor: '#FEF3C7', textColor: '#92400E', borderColor: '#FCD34D' },
  { id: 'AUTHORIZATION', name: 'Authorization', icon: '🔐', bgColor: '#DBEAFE', textColor: '#1E40AF', borderColor: '#93C5FD' },
  { id: 'VALIDATION', name: 'Validation', icon: '✅', bgColor: '#D1FAE5', textColor: '#065F46', borderColor: '#6EE7B7' },
  { id: 'BUSINESS_RULE', name: 'Business Rule', icon: '💼', bgColor: '#E9D5FF', textColor: '#6B21A8', borderColor: '#C084FC' }
];

const ValidationPicker = ({ 
  appName, 
  onSelect, 
  onClose, 
  multiple = false,
  preSelected = []
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [validations, setValidations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedValidations, setSelectedValidations] = useState(preSelected);
  const [previewValidation, setPreviewValidation] = useState(null);
  const [groupedValidations, setGroupedValidations] = useState({});

  useEffect(() => {
    fetchAllValidations();
  }, [appName]);

  useEffect(() => {
    groupValidationsByCategory();
  }, [validations, searchTerm]);

  const fetchAllValidations = async () => {
    if (!appName) return;
    
    try {
      setLoading(true);
      console.log('🔍 Fetching validations for app:', appName);
      
      // Use the validation service API
      const allValidations = await getAllValidations(appName);
      
      console.log('✅ Validations fetched:', allValidations.length);
      setValidations(allValidations);
    } catch (error) {
      console.error('❌ Error fetching validations:', error);
      setValidations([]);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryForValidation = (validation) => {
    // Static templates are identified by validationType field
    if (validation.validationType) {
      return 'STATIC';
    }
    // Business rules use ruleCategory field
    return validation.ruleCategory || 'BUSINESS_RULE';
  };

  const groupValidationsByCategory = () => {
    const filtered = validations.filter(v =>
      v.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      v.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      v.ruleName?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const grouped = {
      STATIC: [],
      AUTHORIZATION: [],
      VALIDATION: [],
      BUSINESS_RULE: []
    };

    filtered.forEach(validation => {
      const category = getCategoryForValidation(validation);
      if (grouped[category]) {
        grouped[category].push(validation);
      }
    });

    setGroupedValidations(grouped);
  };

  const handleCreateNew = () => {
    const returnUrl = encodeURIComponent(location.pathname + location.search);
    navigate(`/dashboard/${appName}/validations/new?returnUrl=${returnUrl}`);
  };

  const handleSelectValidation = (validation) => {
    if (multiple) {
      const isSelected = selectedValidations.find(v => v.id === validation.id);
      if (isSelected) {
        setSelectedValidations(selectedValidations.filter(v => v.id !== validation.id));
      } else {
        setSelectedValidations([...selectedValidations, validation]);
      }
    } else {
      onSelect(validation);
      onClose();
    }
  };

  const handleConfirm = () => {
    if (multiple) {
      onSelect(selectedValidations);
    }
    onClose();
  };

  const getTypeLabel = (validation) => {
    // For static templates, show the validation type
    if (validation.validationType) {
      return validation.validationType.replace(/_/g, ' ');
    }
    // For business rules, show rule category
    return validation.ruleCategory || validation.type || 'Custom';
  };

  const getTotalCount = () => {
    return Object.values(groupedValidations).reduce((sum, arr) => sum + arr.length, 0);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('✓ Copied to clipboard!');
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  };

  const handleViewJSON = (validation, e) => {
    e.stopPropagation();
    setPreviewValidation(validation);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50" onClick={onClose}>
      <div 
        className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Shield size={24} className="text-blue-600" />
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                Select Validation{multiple ? 's' : ''}
              </h2>
              <p className="text-sm text-gray-600">
                Choose from saved validations
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>

        {/* Search */}
        <div className="px-6 py-4 border-b border-gray-100">
          <div className="relative">
            <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search validations by name, description, or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm"
              autoFocus
            />
          </div>
          <div className="mt-2 flex items-center justify-between text-xs">
            <div className="flex items-center gap-4">
              {!loading && (
                <>
                  <span className="text-gray-600">
                    <span className="font-semibold">{getTotalCount()}</span> total validations
                  </span>
                  {VALIDATION_CATEGORIES.map(cat => {
                    const count = groupedValidations[cat.id]?.length || 0;
                    if (count === 0) return null;
                    return (
                      <span 
                        key={cat.id}
                        className="flex items-center gap-1"
                        style={{ color: cat.textColor }}
                      >
                        <span>{cat.icon}</span>
                        <span className="font-semibold">{count}</span>
                      </span>
                    );
                  })}
                </>
              )}
            </div>
            {multiple && selectedValidations.length > 0 && (
              <div className="text-blue-600 font-semibold">
                {selectedValidations.length} selected
              </div>
            )}
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-12 text-gray-400">Loading validations...</div>
          ) : getTotalCount() === 0 ? (
            <div className="text-center py-12">
              <Shield size={48} className="mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500 text-lg font-medium mb-2">No validations found</p>
              <p className="text-gray-400 text-sm mb-4">
                {searchTerm ? 'Try a different search term' : 'Create a new validation to get started'}
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {VALIDATION_CATEGORIES.map(category => {
                const categoryValidations = groupedValidations[category.id] || [];
                if (categoryValidations.length === 0) return null;

                return (
                  <div key={category.id} className="space-y-3">
                    {/* Category Header */}
                    <div 
                      className="flex items-center gap-3 pb-2 border-b-2"
                      style={{ 
                        borderColor: category.borderColor,
                        backgroundColor: category.bgColor,
                        padding: '8px 12px',
                        borderRadius: '8px',
                        marginBottom: '12px'
                      }}
                    >
                      <span style={{ fontSize: '20px' }}>{category.icon}</span>
                      <h3 
                        className="font-bold text-sm uppercase tracking-wide"
                        style={{ color: category.textColor }}
                      >
                        {category.name}
                      </h3>
                      <span 
                        className="ml-auto text-xs font-semibold px-2 py-1 rounded-full"
                        style={{ 
                          backgroundColor: 'white',
                          color: category.textColor
                        }}
                      >
                        {categoryValidations.length}
                      </span>
                    </div>

                    {/* Category Validations */}
                    <div className="grid gap-3 pl-2">
                      {categoryValidations.map(validation => {
                        const isSelected = selectedValidations.find(v => v.id === validation.id);
                        return (
                          <div
                            key={validation.id}
                            onClick={() => handleSelectValidation(validation)}
                            className={`p-4 border-2 rounded-lg cursor-pointer transition ${
                              isSelected
                                ? 'bg-blue-50'
                                : 'border-gray-200 hover:border-blue-300 bg-white'
                            }`}
                            style={{
                              borderColor: isSelected ? '#3B82F6' : category.borderColor,
                              borderLeftWidth: '4px'
                            }}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <h4 className="font-semibold text-gray-900">
                                    {validation.name || validation.ruleName}
                                  </h4>
                                  <span 
                                    className="text-xs px-2 py-0.5 rounded font-medium"
                                    style={{
                                      backgroundColor: category.bgColor,
                                      color: category.textColor
                                    }}
                                  >
                                    {getTypeLabel(validation)}
                                  </span>
                                  {isSelected && multiple && (
                                    <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded font-medium">
                                      ✓ Selected
                                    </span>
                                  )}
                                </div>
                                {validation.description && (
                                  <p className="text-sm text-gray-600 mb-2">{validation.description}</p>
                                )}
                                {/* Show messages for validations */}
                                {(validation.errorMessage || validation.successMessage) && (
                                  <div className="text-xs text-gray-500 mb-2 space-y-1">
                                    {validation.errorMessage && (
                                      <div className="flex items-center gap-1">
                                        <span className="text-red-500">❌</span>
                                        <span>{validation.errorMessage}</span>
                                      </div>
                                    )}
                                    {validation.successMessage && (
                                      <div className="flex items-center gap-1">
                                        <span className="text-green-500">✅</span>
                                        <span>{validation.successMessage}</span>
                                      </div>
                                    )}
                                  </div>
                                )}
                                <div className="flex items-center gap-3">
                                  <span className="text-xs text-gray-500 font-mono">
                                    {validation.id}
                                  </span>
                                  <button
                                    onClick={(e) => handleViewJSON(validation, e)}
                                    className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                                  >
                                    <Code size={12} />
                                    View JSON
                                  </button>
                                </div>
                              </div>
                              {multiple && (
                                <input
                                  type="checkbox"
                                  checked={!!isSelected}
                                  onChange={() => handleSelectValidation(validation)}
                                  className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500 ml-3"
                                />
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center bg-gray-50">
          <button
            onClick={handleCreateNew}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 transition font-medium"
          >
            <Plus size={18} />
            Create New Validation
          </button>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition"
            >
              Cancel
            </button>
            {multiple && selectedValidations.length > 0 && (
              <button
                onClick={handleConfirm}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                Select {selectedValidations.length} Validation{selectedValidations.length > 1 ? 's' : ''}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* JSON Preview Modal */}
      {previewValidation && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60]"
          onClick={() => setPreviewValidation(null)}
        >
          <div 
            className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col m-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Preview Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gradient-to-r from-blue-50 to-indigo-50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-600 rounded-lg">
                  <Code size={20} className="text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">{previewValidation.name}</h3>
                  <p className="text-sm text-gray-600">Validation JSON Configuration</p>
                </div>
              </div>
              <button 
                onClick={() => setPreviewValidation(null)}
                className="text-gray-400 hover:text-gray-600 p-2 hover:bg-white rounded-lg transition"
              >
                <X size={24} />
              </button>
            </div>

            {/* JSON Content */}
            <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
              <div className="bg-gray-900 rounded-xl p-6 overflow-x-auto">
                <pre className="text-green-400 text-sm font-mono leading-relaxed">
                  {JSON.stringify(previewValidation, null, 2)}
                </pre>
              </div>

              {/* Reference for Process JSON */}
              <div className="mt-6 bg-white border-2 border-blue-200 rounded-xl p-6">
                <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <CheckCircle size={18} className="text-blue-600" />
                  Use in Process JSON (Copy Reference)
                </h4>
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <pre className="text-xs font-mono text-gray-800 leading-relaxed overflow-x-auto">
{`{
  "validationId": "${previewValidation.id}",
  "validationJSON": ${JSON.stringify(previewValidation, null, 2).split('\n').map((line, i) => i === 0 ? line : '  ' + line).join('\n')}
}`}
                  </pre>
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
              <div className="text-sm text-gray-600">
                <span className="font-semibold">Validation ID:</span> <code className="bg-gray-200 px-2 py-1 rounded text-xs">{previewValidation.id}</code>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => copyToClipboard(JSON.stringify(previewValidation, null, 2))}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition font-medium"
                >
                  <Copy size={16} />
                  Copy Full JSON
                </button>
                <button
                  onClick={() => copyToClipboard(`{
  "validationId": "${previewValidation.id}",
  "validationJSON": ${JSON.stringify(previewValidation, null, 2)}
}`)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
                >
                  <Copy size={16} />
                  Copy Process Reference
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ValidationPicker;

